import React, {FC, useState} from 'react';
import { useTranslation } from 'react-i18next';
import Modal from "../../../../shared/modal/modal";
import succesIcon from '../../../../images/success.svg'
import rejectIcon from '../../../../images/error-120.svg'
import styles from './verified-modal.module.scss'
import Button from "../../../ui-kit/button/button";
import {StageTypeId, ValidationResult} from "../../../../interfaces/utilities";
import {useAppSelector} from "../../../../hooks/useAppSelector";
import {saveStageFiles} from "../../../../services/api";
import {useNavigate} from "react-router-dom";

interface VerifiedModalProps {
    onClose: () => void;
    validation: ValidationResult;
}

const VerifiedModal: FC<VerifiedModalProps> = ({onClose, validation}) => {
    const { isValid, issues = [] } = validation;
    const {
        selectedRow,
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        convertedGeometry,
        deletedFileIds,
    } = useAppSelector(state => state.masterUtilities)

    const [comment, setComment] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const stageTypeId: StageTypeId =
        selectedRow?.stage === "Design Stage" ? 1 : 2;

    const handlePrimaryClick = async () => {
        if (!selectedRow) return
        if (isValid) {
            try {
                setIsLoading(true);

                const stageKey = stageTypeId === 1 ? "design" : "asBuilt";

                const stageGeometry = convertedGeometry?.[stageKey];

                const files = stageGeometry
                    ? Object.values(stageGeometry).map(file => ({
                        ...file,
                        graphics: file.graphics.map(g => ({
                            ...g,
                            attributes: Object.fromEntries(
                                Object.entries(g.attributes).filter(([key]) => key !== 'uuid')
                            )
                        }))
                    }))
                    : [];


                const payload = {
                    ...(selectedRow.projectId && { projectId: selectedRow.projectId }),
                    stageTypeId,
                    workPermitId: selectedRow.workPermitId,
                    workPermitNumber: selectedRow.workPermitNumber,
                    estateId: selectedRow.workPermitEstateId,
                    estate: selectedRow.workPermitEstate,
                    files,
                    stageTerritoryGeometry: JSON.stringify(stageTerritoryGeometry[stageKey]?.geometry),
                    ...(buildingTerritoryGeometry[stageKey] && {
                        buildingTerritoryGeometry: JSON.stringify(buildingTerritoryGeometry[stageKey]?.geometry)
                    }),
                    ...(comment.trim() && { comment }),
                    ...(deletedFileIds.length > 0 && { deleteFiles: deletedFileIds }),
                };

                await saveStageFiles(payload);
                onClose()
                navigate('/repository/utilities')
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        } else {
            onClose();
        }
    };

    return (
        <Modal closeOnOverlayClick={false} onClose={onClose}>
            <div className={styles.verifiedModal}>
                <h1 className={styles.title}>{t('verified-modal.title')}</h1>
                <img className={styles.image} src={isValid ? succesIcon : rejectIcon} alt={isValid ? t('verified-modal.successAlt') : t('verified-modal.rejectAlt')}/>
                {isValid ? (
                    <div className={styles.qcResult}>
                        <span className={styles.qcResultMessage}>{t('verified-modal.matchCriteria')}</span>
                        <div className={styles.qcResultComment}>
                            <span className={styles.qcResultCommentLabel}>{t('verified-modal.commentLabel')}</span>
                            <textarea
                                className={`${styles.qcResultTextarea} ${comment ? styles.filled : ''}`}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={t('verified-modal.commentPlaceholder')}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.qcResult}>
                            <span className={styles.qcResultMessage}>{t('verified-modal.noMatchCriteria')}</span>
                            <p className={styles.qcResultSuggestion}>{t('verified-modal.suggestion')}</p>
                        </div>
                        {issues.length > 0 && (
                            <div className={styles.gridWrapper}>
                                <div className={styles.gridHeader}>
                                    <div className={`${styles.gridCell} ${styles.header}`}>{t('verified-modal.fileName')}</div>
                                    <div className={`${styles.gridCell} ${styles.header}`}>{t('verified-modal.reason')}</div>
                                </div>
                                <div className={styles.gridBody}>
                                    {issues.map((issue, index) => (
                                        <div className={styles.gridRow} key={index}>
                                            <div className={styles.gridCell}>{issue.layerName}</div>
                                            <div className={styles.gridCell}>{issue.reason}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div className={styles.groupBtn}>
                    <Button
                        type='primary'
                        className={styles.btn}
                        onClick={handlePrimaryClick}
                        disabled={isLoading}
                    >
                        {isValid ? t('verified-modal.sent') : t('verified-modal.importNew')}
                    </Button>
                    <Button
                        type='outline'
                        className={styles.btn}
                        onClick={onClose}
                    >
                        {t('verified-modal.cancel')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default VerifiedModal;
